/**
 * Progresso do boot — derivado das etapas REAIS, não de um cronômetro.
 *
 * ── A regra que decide o desenho ────────────────────────────────────────────
 * Barra de progresso que avança sozinha com o relógio é decoração que mente: ela
 * chega a 90% e para, ou completa enquanto o app continua parado. Aqui o
 * preenchimento é função de **quantas etapas do boot já assentaram** — as mesmas
 * que o painel de log mostra. Cada avanço da barra corresponde a uma coisa que
 * de fato aconteceu.
 *
 * ── Por que uma curva que satura, e não "etapa N de 5" ──────────────────────
 * O número de etapas VARIA com o estado: sem sessão o boot é `main` →
 * `oauth-redirect` → `auth-state` e acabou; com sessão entram ainda
 * `ensure-profile`, `profile-snapshot` e `beta-verdict`. Um denominador fixo
 * mentiria numa das duas pontas — ou a barra pula de 60% para o app, ou ela
 * empaca em 50% quando já terminou.
 *
 * `1 - k^n` resolve isso sem precisar saber o total: cada etapa concluída cobre
 * uma fração do que falta, então a barra sempre avança de forma perceptível,
 * nunca regride e **nunca chega a 100%** — quem "completa" a barra é o app
 * aparecendo, que é a única definição honesta de boot terminado.
 */

import { useSyncExternalStore } from "react";
import { getBootLog, subscribeBootLog, type BootEntry } from "./bootTrace";

/** Fases que contam como "etapa assentada". `start` e `info` não avançam nada. */
const FASES_TERMINAIS: ReadonlySet<BootEntry["phase"]> = new Set([
  "ok",
  "fail",
  "timeout",
  "skip",
]);

/**
 * Fração do que FALTA coberta por cada etapa. 0,62 dá a escada
 * 38% · 62% · 76% · 85% · 91% — passos grandes no começo (quando a pessoa está
 * decidindo se o app travou) e cada vez menores no fim.
 */
const DECAIMENTO = 0.62;

/**
 * Piso visual. Uma barra que nasce em zero parece uma barra quebrada; este é o
 * único número aqui que é estética, e ele não finge progresso — é o "começou".
 */
const PISO = 0.08;

/** Progresso em 0..1 a partir de um log de boot. Puro, para poder ser testado. */
export function computeBootProgress(log: readonly BootEntry[]): number {
  const concluidas = new Set<string>();
  for (const e of log) {
    if (FASES_TERMINAIS.has(e.phase)) concluidas.add(e.step);
  }
  const bruto = 1 - Math.pow(DECAIMENTO, concluidas.size);
  return Math.max(PISO, Math.min(bruto, 0.97));
}

/** Progresso vivo do boot, para a tela de abertura. */
export function useBootProgress(): number {
  const log = useSyncExternalStore(subscribeBootLog, getBootLog, getBootLog);
  return computeBootProgress(log);
}

/**
 * Microcopy por ETAPA (17/09/2026, tela de abertura nova).
 *
 * A frase acompanha a etapa que está em voo — a mesma que o painel de log
 * mostra —, então ela muda porque algo aconteceu, nunca por relógio. A regra
 * é a mesma da barra: comunicar estado real. Uma etapa que não conhecemos
 * (marcos avulsos como `firebase-init`, `device`, `resume:*`) não muda nada.
 *
 * A frase padrão é a que o placeholder NATIVO já desenha antes de o JS rodar
 * (`scripts/mobile-postbuild.mjs`): a troca de camadas não muda o texto.
 */
export const BOOT_STAGE_DEFAULT = "Preparando sua estante…";

const BOOT_STAGE_COPY: Readonly<Record<string, string>> = {
  "auth-state": "Verificando sua sessão…",
  "ensure-profile": "Buscando seu perfil…",
  "profile-snapshot": "Abrindo sua estante…",
  "beta-verdict": "Verificando seu acesso…",
};

/**
 * Frase para a etapa em voo: a ÚLTIMA etapa conhecida que começou e ainda não
 * assentou. Puro, para poder ser testado.
 */
export function describeBootStage(log: readonly BootEntry[]): string {
  const pendente = new Map<string, true>();
  for (const e of log) {
    if (!(e.step in BOOT_STAGE_COPY)) continue;
    if (e.phase === "start") pendente.set(e.step, true);
    else if (FASES_TERMINAIS.has(e.phase)) pendente.delete(e.step);
  }
  let ultima: string | null = null;
  for (const step of pendente.keys()) ultima = step;
  return ultima ? BOOT_STAGE_COPY[ultima] : BOOT_STAGE_DEFAULT;
}

/** Microcopy viva do boot, para a tela de abertura. */
export function useBootStage(): string {
  const log = useSyncExternalStore(subscribeBootLog, getBootLog, getBootLog);
  return describeBootStage(log);
}
